import { closestCorners, CollisionDetection, DndContext, DragEndEvent, DragOverEvent, DragStartEvent, pointerWithin, rectIntersection } from '@dnd-kit/core';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { ImmerFuncs } from '../../../../external/immer/immer.funcs';
import { ServerApi } from '../../../../external/server/server.api';
import { CssFuncs } from '../../../../foundation/css/css.funcs';
import { ArrayFuncs } from '../../../../foundation/js/array.funcs';
import { JsFuncs } from '../../../../foundation/js/js.funcs';
import { BasicNodeSubWireFuncs } from '../../../core/node/basic-node-sub.wire.funcs';
import { NodeService } from '../../../core/node/node.service';
import { NodeStore } from '../../../core/node/node.store';
import { CentralDragAndDropStore } from './central-drag-and-drop.store';
import { CentralDragAndDropStyle } from './central-drag-and-drop.style';

export function CentralDragAndDrop({ children }: { children: ReactNode }) {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setIsDragging(true);
    const handlers = CentralDragAndDropStore.get();
    const handler = handlers[event.active.data.current?.type];
    handler?.onDragStart?.(event);
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const handlers = CentralDragAndDropStore.get();
    const handler = handlers[event.active.data.current?.type];
    handler?.onDragOver?.(event);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const handlers = CentralDragAndDropStore.get();
    const handler = handlers[event.active.data.current?.type];
    handler?.onDragEnd?.(event);
    setIsDragging(false);
  }, []);

  const collisionDetectionStrategy: CollisionDetection = useCallback((args) => {
    const type = args.active.data.current?.type;
    let droppableContainers = args.droppableContainers.filter((c) => c.data.current?.type === type);

    if (type === 'node') {
      const subNodeIds = NodeService.findAllSubNodeIds(args.active.id as string);
      droppableContainers = droppableContainers.filter((c) => {
        const parentId = c.data.current?.parentId;
        const nodeId = c.data.current?.nodeId;
        if (nodeId === args.active.id) {
          return false;
        }

        if (subNodeIds.includes(parentId)) {
          return false;
        }

        return true;
      });
    }

    let intersections = pointerWithin({ ...args, droppableContainers });
    if (ArrayFuncs.isNotEmpty(intersections)) {
      return intersections;
    }

    intersections = rectIntersection({ ...args, droppableContainers });
    if (ArrayFuncs.isNotEmpty(intersections)) {
      return intersections;
    }

    const corners = closestCorners({ ...args, droppableContainers });
    return corners;
  }, []);

  useEffect(() => {
    function handleDragEnd(e: DragEndEvent) {
      console.log('active', e.active.id, e.active.data.current);
      console.log('over', e.over?.id, e.over?.data.current);

      const starting = e.active.data.current as { nodeId: string; parentId: string };
      const ending = e.over?.data.current as { nodeId?: string; parentId: string };

      if (JsFuncs.isNull(starting.parentId) || JsFuncs.isNull(ending.parentId)) {
        return;
      }

      // case 1: same parent
      if (starting.parentId === ending.parentId) {
        const parentNode = NodeStore.getItem(starting.parentId);

        const sub = ImmerFuncs.produce(ArrayFuncs.ensure(parentNode?.sub), (draft) => {
          BasicNodeSubWireFuncs.removeItem(draft, { child: starting.nodeId });

          if (ending.nodeId) {
            BasicNodeSubWireFuncs.insertItemBeforeItem(draft, { child: starting.nodeId, before: ending.nodeId });
          } else {
            draft.push(starting.nodeId);
          }
        });

        NodeStore.updateItem(starting.parentId, { sub });
        ServerApi.updateNode(starting.parentId, { sub });
        //
      } else {
        // case 2: different parent
        // today to board: remove from today, add to board
        // board to today: not remove from board, add to today
        // board to board: remove from board, add to board
        // conclusion, when board to today, not remove from board
        const startingParentNode = NodeStore.getItem(starting.parentId)!;
        const endingParentNode = NodeStore.getItem(ending.parentId)!;
        const startingParentIsToday = startingParentNode.purpose === 'today';
        const endingParentIsToday = endingParentNode.purpose === 'today';

        // add to ending parent
        (() => {
          const sub = ImmerFuncs.produce(ArrayFuncs.ensure(endingParentNode?.sub), (draft) => {
            if (ending.nodeId) {
              BasicNodeSubWireFuncs.insertItemBeforeItem(draft, { child: starting.nodeId, before: ending.nodeId });
            } else {
              draft.push(starting.nodeId);
            }
          });

          NodeStore.updateItem(ending.parentId, { sub });
          ServerApi.updateNode(ending.parentId, { sub });
        })();

        // remove from starting parent
        (() => {
          if (!startingParentIsToday && endingParentIsToday) {
            return;
          }

          const sub = ImmerFuncs.produce(ArrayFuncs.ensure(startingParentNode?.sub), (draft) => {
            BasicNodeSubWireFuncs.removeItem(draft, { child: starting.nodeId });
          });

          NodeStore.updateItem(starting.parentId, { sub });
          ServerApi.updateNode(starting.parentId, { sub });
        })();
      }
    }

    CentralDragAndDropStore.register({ type: 'node', handler: { onDragEnd: handleDragEnd } });
  }, []);

  return (
    <DndContext onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd} collisionDetection={collisionDetectionStrategy}>
      <CentralDragAndDropStyle.Container
        className={CssFuncs.className((names) => {
          names.push(isDragging ? CentralDragAndDropStyle.ContainerClassNames.isDragging : CentralDragAndDropStyle.ContainerClassNames.isNotDragging);
        })}
      >
        {children}
      </CentralDragAndDropStyle.Container>
    </DndContext>
  );
}
